import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SEO from "../components/SEO";
import TutorCenterDetail from "../components/tutorCenters/TutorCenterDetail";
import mainData from "../jsonDB/main.json";
import mediaData from "../jsonDB/media_updated.json";

function slugify(text) {
  if (!text) return "";
  return (
    text
      .toString()
      .toLowerCase()
      .normalize("NFKD")
      .replace(/\p{Diacritic}/gu, "")
      // allow Unicode letters and numbers (keeps Chinese characters)
      .replace(/[^^\p{L}\p{N}]+/gu, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
  );
}

export default function TutorCenterDetailPage() {
  const { id, slug } = useParams();
  const navigate = useNavigate();

  const center = useMemo(() => {
    if (!id) return null;
    const raw = (mainData || []).find((c) => c.id === id) || null;
    if (!raw) return null;
    // find media entry by id and merge images/product_tags/url if present
    try {
      const mediaMap = (mediaData || []).reduce((acc, m) => {
        if (m && m.id) acc[m.id] = m;
        return acc;
      }, {});
      const media = mediaMap[id];
      return {
        ...raw,
        images:
          raw.images && raw.images.length > 0
            ? raw.images
            : (media && media.images) || [],
        product_tags:
          raw.product_tags && raw.product_tags.length > 0
            ? raw.product_tags
            : (media && media.product_tags) || [],
        url: raw.url || (media && media.url) || "",
      };
    } catch (e) {
      return raw;
    }
  }, [id]);

  // If center exists but slug is missing or doesn't match, redirect to canonical URL with slug
  useEffect(() => {
    try {
      if (!center) return;
      const expected = slugify(center.title || center.id || "");
      if (!expected) return;
      if (!slug || slug !== expected) {
        navigate(`/tutorCenters/${id}/${expected}`, { replace: true });
      }
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center, id, slug]);

  // canonical URL and SEO meta
  const canonicalSlug = slugify(center?.title || center?.id || "");
  const canonicalUrl =
    typeof window !== "undefined" && center
      ? `${window.location.origin}/tutorCenters/${id}/${canonicalSlug}`
      : "";
  useEffect(() => {
    if (!canonicalUrl) return;
    try {
      let link = document.querySelector("link[rel='canonical']");
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonicalUrl);
    } catch (e) {}
  }, [canonicalUrl]);

  // ensure page starts at top when navigating to detail
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch (e) {}
  }, []);

  if (!center) {
    return (
      <div className="page p-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-16">
            <p className="text-lg text-gray-600">Center not found</p>
            <div className="mt-6">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page p-4">
      <SEO
        title={center.title}
        description={(center.content_text || "").slice(0, 160)}
        url={canonicalUrl}
      />
      <div className="max-w-4xl mx-auto">
        <TutorCenterDetail center={center} onBack={() => navigate(-1)} />
      </div>
    </div>
  );
}
